import { FC, useState, useEffect } from 'react';

import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
} from '@mui/material';

import { AccountingPeriodDialogProps, Period, PeriodStatus } from './types';
import { generatePeriodId } from './utils';
import { Dialog } from '@/components/ui/Dialog';

const AccountingPeriodDialog: FC<AccountingPeriodDialogProps> = ({
  period,
  open,
  onClose,
  onSave,
  title,
}) => {
  const [form, setForm] = useState<Period | null>(period);

  useEffect(() => setForm(period), [period]);

  const handleChange = (key: keyof Period, value: string) => {
    if (!form) return;
    setForm({ ...form, [key]: value } as Period);
  };

  if (!form) return null;

  const isNewPeriod = period && period.id.includes('new-');
  const dialogTitle = title || (isNewPeriod ? 'Add period' : 'Edit period');
  const idSuggestion = generatePeriodId(form.startDate);

  const statusOptions: PeriodStatus[] = [
    'Open',
    'Closed for registration',
    'Closed for invoicing',
    'Closed',
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Start date"
            type="date"
            value={form.startDate}
            onChange={(e) => handleChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End date"
            type="date"
            value={form.endDate}
            onChange={(e) => handleChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            select
            label="Status"
            value={form.status}
            onChange={(e) => handleChange('status', e.target.value)}
            fullWidth
          >
            {statusOptions.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Identifier (optional)"
            helperText="Used internally to identify the period"
            value={form.id}
            onChange={(e) => handleChange('id', e.target.value)}
            placeholder={idSuggestion}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { AccountingPeriodDialog };
